import {
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
  Signal,
  signal,
  viewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import {FeedbackService, ResizedDirective,} from 'frontier/browserkit';
import {EControlActions, IControlObject} from 'frontier/nucleus';
import {catchError, concatMap, tap} from "rxjs/operators";
import {Observable, of, Subscription} from "rxjs";
import {IClientApi, IClientsTileApi} from './clients-tile.interface';
import {GridApi} from 'ag-grid-community';
import {CartSelectionComponent} from './cart-selection/cart-selection.component';
import {ICartControlObj} from './cart-selection/cart-control-obj.interface';
import {ClientsComparatorTableComponent} from '../clients-comparator-table/clients-comparator-table.component';
import {PdfDialogComponent} from 'frontier/browserkit/src/lib/dialogs/pdf-dialog/pdf-dialog.component';
import {ClientMessageDetailsComponent} from './client-message-details/client-message-details.component';
import {NotBillableFormComponent} from './not-billable-form/not-billable-form.component';
import {ControlStore} from 'frontier/nucleus/src/lib/apiv2/control.store';
import {AgGridModule} from 'ag-grid-angular';
import {MatProgressBar} from '@angular/material/progress-bar';
import {ICart} from '../../service/state.interface';
import {MatDialogRef} from '@angular/material/dialog';
import {
  ClientComparatorNotBillableFormTemplateComponent
} from "../../../client-comparator-not-billable-form-template/client-comparator-not-billable-form-template.component";
import {MatButton} from "@angular/material/button";
import {AppService} from "../../../../../api/generated";

interface IClientreplacement {
  client: IClientApi;
  cart: IControlObject;
}

@Component({
  selector: 'kpi4me-conspicuity-detail',
  templateUrl: './conspicuity-detail.component.html',
  styleUrls: ['./conspicuity-detail.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NotBillableFormComponent,
    AgGridModule,
    ClientMessageDetailsComponent,
    ClientsComparatorTableComponent,
    MatProgressBar,
    ResizedDirective,
    MatButton,
  ],
})
export class ConspicuityDetailComponent extends ClientComparatorNotBillableFormTemplateComponent implements OnDestroy {
  private _controlStore: ControlStore = inject(ControlStore);
  private _feedbackService: FeedbackService = inject(FeedbackService);
  private _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _appService: AppService = inject(AppService);

  @Output() clientReplaced: EventEmitter<IClientreplacement> = new EventEmitter<IClientreplacement>();

  clientMessageDetails: Signal<ClientMessageDetailsComponent> = viewChild(ClientMessageDetailsComponent);
  clientsComparatorTable: Signal<ClientsComparatorTableComponent> = viewChild(ClientsComparatorTableComponent);

  conspicuityGridApi: GridApi;
  subs: Subscription = new Subscription();

  showForm: WritableSignal<boolean> = signal<boolean>(false);

  previousCart: IControlObject | null = null;

  constructor() {
    super();
    effect(() => {
      const selectedCart: ICart = this.selectedCart();
      if (selectedCart && selectedCart.obj?.signature !== this.previousCart?.signature) {
        this.refresh()
      }
    }, {allowSignalWrites: true})

    this.subs.add(this._processOverviewService.replacedClientForCart$.subscribe(() => {
      if (this.selectedCart()) {
        this.refresh()
      }
    }))
  }

  private refresh() {
    this.previousCart = this.selectedCart().obj;
    this.showForm.set(false);
    this.clientsMessage.set(null);
    this.rowData.set(null);
    this.clients.set([]);
    this._cdr.detectChanges();
    this.showForm.set(true);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onTakeDataOfClient(client: IClientsTileApi): void {
    this._notBillableService.notBillableGetCartsOfClient({
      InstanceId: this.notBillableFormRef().instanceId()
    }).pipe(
      concatMap(
        (otherCartsOfClient: IControlObject[]) => {
          const dialogRef: MatDialogRef<CartSelectionComponent> = this._dialogService.dialog.open(
            CartSelectionComponent,
            {
              data: {
                client,
                selectedCart: this.selectedCart().obj,
                carts: otherCartsOfClient,
              },
            }
          )
          return dialogRef.afterClosed();
        }
      ),
      concatMap((carts: ICartControlObj[] | null): Observable<boolean> => {
        if (carts) {
          return this._notBillableService.notBillableReplaceClientForCart(
            {
              InstanceId: this.notBillableFormRef().instanceId(),
              client: client.client,
              SelectedCarts: carts,
            }).pipe(
            tap(
              () => {
                this.clientReplaced.emit({
                  client: client.client,
                  cart: carts.find((r: ICartControlObj): boolean => r.signature === this.selectedCart().obj.signature),
                });
              }
            ))
        }
        return of(null);
      }),
    ).subscribe();
  }

  onResolveAutomatically(): void {
    this._notBillableService.notBillableExecuteClientMessage({
      InstanceId: this.notBillableFormRef().instanceId()
    })
      .pipe(
        tap(() => {
            this._controlStore.controlDataChanged$.next({
              GUID: null,
              changeType: EControlActions.resolveClientMessage
            });
            this.clientsMessage.set(undefined);
          }
        ),
        catchError(() => {
            this._feedbackService.setError('Fehler beim Ausführen der Client-Nachricht');
            return of(null);
          }
        ),
      ).subscribe();
  }

  onResolveManually(): void {
    this._notBillableService.notBillableResolveClientMessageManual({
      InstanceId: this.notBillableFormRef().instanceId()
    })
      .pipe(
        tap(() => {
            this._controlStore.controlDataChanged$.next({
              GUID: null,
              changeType: EControlActions.resolveClientMessage
            });
            this.clientsMessage.set(undefined);
          }
        ),
        catchError(() => {
            this._feedbackService.setError('Fehler beim manuellen Lösen der Client-Nachricht');
            return of(null);
          }
        ),
      ).subscribe();
  }

  onShowDocument(): void {
    this._notBillableService.notBillableDownloadClientMessageDocument({
      InstanceId: this.notBillableFormRef().instanceId()
    })
      .pipe(
        tap((response: Blob) => {
          if (response) {
            this._dialogService.dialog.open(PdfDialogComponent, {
              data: response,
              width: '80vw',
              height: '90vh'
            });
          } else {
            this._feedbackService.setError('Fehler beim Anzeigen des Dokuments');
          }
        }),
        catchError(() => {
            this._feedbackService.setError('Fehler beim Anzeigen des Dokuments');
            return of(null);
          }
        ),
      ).subscribe();
  }

  resized(): void {
    [
      this.conspicuityGridApi,
      this.clientMessageDetails()?.gridApi,
      this.clientsComparatorTable()?.gridApi,
    ].forEach((api: GridApi) => {
      if (api) {
        api.sizeColumnsToFit();
      }
    });
  }

  separateClientReference(): void {
    this._notBillableService.notBillableSeperateClientReference(
      {InstanceId: this.notBillableFormRef().instanceId()}).subscribe(client => {
      this.clientReplaced.emit({
        client: client as IClientApi,
        cart: this.selectedCart().obj,
      });
    });
  }

  approveUncollectibleRequest(): void {
    this._appService.notBillableApproveUncollectibleRequest({InstanceId: this.notBillableFormRef().instanceId()})
      .subscribe((res: boolean): void => {
        if(res) {
          this.notBillableFormRef().changeAndFetchInstance();
        }
      });
  }

  declineUncollectibleRequest(): void {
    this._appService.notBillableDeclineUncollectibleRequest({InstanceId: this.notBillableFormRef().instanceId()})
      .subscribe((res: boolean): void => {
        if(res) {
          this.notBillableFormRef().changeAndFetchInstance();
        }
      });
  }
}
